/**
 * Helpers for resolving a stored UUID in a custom user-multi field to a
 * friendly name + kind, paralleling the SQL `risksmart.user_or_group` view.
 *
 * Custom user-multi fields store flat `string[]` of UUIDs. Each id may
 * point at a user (auth.user via the active-users view) or a user_group.
 * Web consumers fetch both lookups via `useAddCustomAttributeFieldData`
 * and pass them into `customFieldValue`, which then calls these helpers.
 */

export type UserOrGroupLookups = {
  userLookup: Record<string, string> | undefined;
  groupLookup: Record<string, string> | undefined;
};

export type UserOrGroupKind = 'user' | 'userGroup';

export type UserOrGroup = {
  id: string;
  name: string;
  kind: UserOrGroupKind;
};

/**
 * Resolve `id` to its kind + friendly name. Users take precedence over
 * groups (UUIDs are namespace-disjoint in practice, but the explicit
 * order keeps the contract stable). Returns `null` for orphans —
 * callers decide whether to drop, render the raw UUID, or run a
 * separate audit-table lookup for deleted-group `(deleted)` markers.
 */
export const resolveUserOrGroup = (
  id: string,
  { userLookup, groupLookup }: UserOrGroupLookups
): UserOrGroup | null => {
  const userName = userLookup?.[id];
  if (userName) {
    return { id, name: userName, kind: 'user' };
  }

  const groupName = groupLookup?.[id];
  if (groupName) {
    return { id, name: groupName, kind: 'userGroup' };
  }

  return null;
};

/**
 * Convenience wrapper for the most common consumer path — just the name.
 * Equivalent to `resolveUserOrGroup(id, lookups)?.name ?? null`.
 */
export const resolveUserOrGroupName = (
  id: string,
  lookups: UserOrGroupLookups
): string | null => resolveUserOrGroup(id, lookups)?.name ?? null;
